import { HATAG } from '@business/constants/helparticle';
import { SubcontractorStatus, UserRole } from '@business/entities/account';
import { AppContext, GCApp } from '@core/components/AppContextProvider';
import { PrivateRoute, Redirect, Router, PublicRoute } from '@core/router';
import { Session } from '@modules/auth/session';
import HelpArticlePage from '@modules/helpArticles/HelpArticlePage';
import HelpArticlesPage from '@modules/helpArticles/HelpArticlesPage';
import CheckListDetailPage from '@modules/marketing/CheckListDetailPage';
import TodosPage from '@modules/TodosPage';
import React, { lazy, memo } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ViewProposalPage } from '@modules/proposals/ProposalsPage/ViewProposalPage';
import { PermissionService } from '@business/services/permission_service';
import { Switch } from 'react-router-dom';
import TMChangeOrderPreviewPage from '@modules/timeMaterials/TMChangeOrderPreviewPage';
import { RoleService } from '@business/services';
import { ServiceProviderPage } from '@modules/vendors/ServiceProviderPage';
import { PayablesPage } from '@modules/vendors/PayablesPage';
// import TemplateBuilderPage from '@modules/settings/SettingsPage/Components/TemplateTab/contract/ContractBuilderPage';

// import AddEditTMChangeOrderPage from '@modules/timeMaterials/AddEditTMChangeOrderPage';

const UpgradeOptionPreviewPage = lazy(() => import('@modules/ClientPortal/home/HomeWidgets/UpgradeOptions/PreviewUpgrade'));
const ListItemPreviewPage = lazy(() => import('@modules/ClientPortal/home/HomeWidgets/ListItems/PreviewListItem'));
const NotificationsPage = lazy(() => import('@modules/notifications'));
const WorkOrderPage = lazy(() => import('@modules/ClientPortal/Dashboard/WorkOrderPage'));
const WorkOrderDashboardPage = lazy(() => import('@modules/ClientPortal/WorkOrderDashboard'));
const ProjectDashboardPage = lazy(() => import('@modules/ClientPortal/ProjectDashboard'));
const ClientDashBoardPage = lazy(() => import('@modules/ClientPortal/Dashboard'));
const ClientProposalPage = lazy(() => import('@modules/ClientPortal/ClientProposal'));
const ReportsPage = lazy(() => import('@modules/reports'));
const WorkOrderDetailPage = lazy(() => import('@modules/subContractor/Dashboard/WorkOrder/WorkOrderDetail'));
const SubContractorDashboardPage = lazy(() => import('@modules/subContractor/Dashboard'));
const EstimatesPage = lazy(() => import('@modules/EstimatesPage'));
const EstimatePage = lazy(() => import('@modules/EstimatesPage/EstimatePage'));
const AdvancedReportingPage = lazy(() => import('@modules/reports/AdvancedReportingPage'));
const CompanyOverviewPage = lazy(() => import('@modules/CompanyOverviewPage'));
const MediaPage = lazy(() => import('@modules/media'));
const CompaniesPage = lazy(() => import('@modules/companies/CompaniesPage'));
const CompanyPage = lazy(() => import('@modules/companies/CompanyPage'));
const OrganizationsPage = lazy(() => import('@modules/organizations/OrganizationsPage'));
const OrganizationsDashboard = lazy(() => import('@modules/organizations/OrganizationsDashboard'));
const ClientDashboard = lazy(() => import('@modules/organizations/ClientDashboard'));
const SubContractorPage = lazy(() => import('@modules/subContractor/SubContractorPage'));
const OrganizationPage = lazy(() => import('@modules/organizations/OrganizationPage'));
const ContactsPage = lazy(() => import('@modules/people/ContactsPage'));
const ContactPage = lazy(() => import('@modules/people/ContactPage'));
const SettingsPage = lazy(() => import('@modules/settings/SettingsPage'));
const MyAccountPage = lazy(() => import('@modules/account/MyAccountPage'));
const UsersPage = lazy(() => import('@modules/users/UsersPage'));
const ReviewSubContractorProfile = lazy(() => import('@modules/users/SubContractorReview'));
const ActivityLogPage = lazy(() => import('@modules/activities/ActivityLogPage'));
const UserRolesPage = lazy(() => import('@modules/user-roles/UserRolesPage'));
const UserRolePermissionPage = lazy(() => import('@modules/user-roles/UserRolePermissionPage'));
const CreateProjectPage = lazy(() => import('@modules/projects/CreateProjectPage'));
const CreateSimpleProjectPage = lazy(() => import('@modules/projects/CreateProjectPage/CreateSimpleProjectPage'));
const EditProjectPage = lazy(() => import('@modules/projects/EditProjectPage'));
const CreateSubscriptionPage = lazy(() => import('@modules/subscription/CreateSubscriptionPage'));
const SubOneTimePayment = lazy(() => import('@modules/subscription/SubOneTimePayment'));
const EditSubscription = lazy(() => import('@modules/subscription/EditSubscription'));
const BoardPage = lazy(() => import('@modules/board/BoardPage'));
const TemplatesPage = lazy(() => import('@modules/templates/TemplatesPage'));
const TMTemplatesPage = lazy(() => import('@modules/timeMaterials/TimeMaterialOverlay/Tabs/TimeMaterialTemplates/TemplatesPage'));
const TemplatePage = lazy(() => import('@modules/templates/TemplatePage'));
const TMTemplatePage = lazy(() => import('@modules/timeMaterials/TimeMaterialOverlay/Tabs/TimeMaterialTemplates/TemplatePage'));
const MarketingBoardPageV2 = lazy(() => import('@modules/MarketingV2/MarketingBoardPage'));
const CheckListPage = lazy(() => import('@modules/marketing/CheckListPage'));
const TicketsPage = lazy(() => import('@modules/support/TicketsPage'));
const TicketDetailPage = lazy(() => import('@modules/support/TicketDetailPage'));
const MyTicketsPage = lazy(() => import('@modules/support/MyTicketsPage'));
const SupportCategoryPage = lazy(() => import('@modules/support/CategoryPage'));
const DealBoardPage = lazy(() => import('@modules/dealBoard/DealBoardPage'));
const AddDealPage = lazy(() => import('@modules/deals/AddDealPage'));
const EditDealPage = lazy(() => import('@modules/deals/EditDealPage'));
const DealPage = lazy(() => import('@modules/deals/DealPage'));
const RegionsMapPage = lazy(() => import('@modules/regions/RegionsMapPage').then(m => ({ default: m.RegionsMapPage })));
const TeamsPage = lazy(() => import('@modules/teams/TeamsPage'));
const ClientProjects = lazy(() => import('@modules/clientProjects'));
const ClientProject = lazy(() => import('@modules/clientProjects/clientProject'));
const DashboardPage = lazy(() => import('@modules/dashboards/DashboardPage'));
const FormPage = lazy(() => import('@modules/public/FormPage'));
const ClientFormPage = lazy(() => import('@modules/public/ClientFormPage'));
const ClientForms = lazy(() => import('@modules/ClientForms'));
const ClientMediaPage = lazy(() => import('@modules/ClientMedia'));
const BrandDetailPage = lazy(() => import('@modules/companies/CompanyPage/BrandDetailPage'));
const LibraryPage = lazy(() => import('@modules/LibraryPage'));
const ChecklistsPage = lazy(() => import('@modules/todoTemplates/ChecklistsPage'));
const ChecklistTemplatePage = lazy(() => import('@modules/todoTemplates/ChecklistTemplatePage'));
const CoursesPage = lazy(() => import('@modules/course/CoursesPage'));
const CoursePage = lazy(() => import('@modules/course/CoursePage'));
const ManageCoursePage = lazy(() => import('@modules/course/ManageCoursePage'));
const TasksPage = lazy(() => import('@modules/tasks/TasksPage'));
const OrdersPage = lazy(() => import('@modules/orders/OrdersPage'));
const AddEditOrderPage = lazy(() => import('@modules/orders/AddEditOrderPage'));
const VendorsPage = lazy(() => import('@modules/orders/VendorsPage'));
const ProductsPage = lazy(() => import('@modules/orders/ProductsPage'));
const PurchaseOrdersPage = lazy(() => import('@modules/orders/PurchaseOrdersPage'));
const IdeasPage = lazy(() => import('@modules/ideas/IdeasPage'));
const IdeaPage = lazy(() => import('@modules/ideas/IdeaPage'));
const EmptyPage = lazy(() => import('@modules/empty/EmptyPage'));
const SetEmailPage = lazy(() => import('@modules/account/SetEmailPage'));
const AddEditOrderInvoicePage = lazy(() => import('@modules/orders/AddEditOrderInvoicePage'));
const EditPOPage = lazy(() => import('@modules/orders/EditPOPage'));
const POLadingPage = lazy(() => import('@modules/orders/POLadingPage/POLadingPage'));
const POPickListPage = lazy(() => import('@modules/orders/POLadingPage/POPickListPage'));
const POLadingPageTM = lazy(() => import('@modules/timeMaterials/POLadingPage/POLadingPage'));
const POPickListPageTM = lazy(() => import('@modules/timeMaterials/POLadingPage/POPickListPage'));
const SharedProjectsPage = lazy(() => import('@modules/sharedProjects'));
const GlossaryPage = lazy(() => import('@modules/course/GlossaryPage'));
const LineUpPage = lazy(() => import('@modules/course/LineUpPage'));
const LineUpDetailPage = lazy(() => import('@modules/course/LineUpPage/LineUpDetailPage'));
const DynamicReportPage = lazy(() => import('@modules/dynamicReports/DynamicReportPage'));
const ChangelogsPage = lazy(() => import('@modules/changelogs/ChangelogsPage'));
const PreviewCoursePage = lazy(() => import('@modules/course/CoursePage/PreviewCoursePage'));
const CommissionsPage = lazy(() => import('@modules/commissions'));
const ProposalBuilderPage = lazy(() => import('@modules/proposal/ProposalBuilderPage'));
const ProjectListTemplateLoader = lazy(() => import('@modules/proposal/ProjectListTemplateLoader'));
const TemplateBuilderPage = lazy(() => import('@modules/settings/SettingsPage/Components/TemplateTab/template/TemplateBuilderPage'));
const ProposalTemplatePage = lazy(() => import('@modules/proposalTemplates/ProposalTemplatePage'));
const SharedProjectBoardPage = lazy(() => import('@modules/board/BoardPage/SharedProjectBoardPage'));
const ShareProjectBoardPage = lazy(() => import('@modules/board/BoardPage/ShareProjectBoardPage'));
const ProposalsPage = lazy(() => import('@modules/proposals/ProposalsPage'));
// const ProposalsPreviewPage = lazy(() => import('@modules/proposals/ViewProposalModal/ProposalPreviewPage'));
const CourseBuilderPage = lazy(() => import('@modules/course/CourseBuilder/CourseBuilderPage'));
const RedirectTodosPage = lazy(() => import('@modules/RedirectTodosPage'));
const PackagesPage = lazy(() => import('@modules/orders/PackagesPage'));
const PackagePage = lazy(() => import('@modules/orders/PackagePage'));
const TimeMaterialsPage = lazy(() => import('@modules/timeMaterials/TimeMaterialsPage'));
const AddEditTimeMaterialPage = lazy(() => import('@modules/timeMaterials/AddEditTimeMaterialPage'));
const AddEditTimeMaterialInvoicePage = lazy(() => import('@modules/timeMaterials/AddEditTimeMaterialInvoicePage'));
const FormsPage = lazy(() => import('@modules/Forms/FormsPage'));
const ManagementFormTemplatesPage = lazy(() => import('@modules/Forms/ManagementFormTemplatesPage'));
const OpenEmailLinkPage = lazy(() => import('@modules/OpenEmailLinkPage'));
const OpenProjectLinkPage = lazy(() => import('@modules/OpenProjectLinkPage'));
const ClientHomePage = lazy(() => import('@modules/ClientHomePage'));
const VendastaAccountsPage = lazy(() => import('@modules/organizations/VendastaAccountsPage'));
const VendastaBillingReportPage = lazy(() => import('@modules/organizations/VendastaBillingReportPage'));
const ClockinReports = lazy(() => import('@modules/clockin'));
const HomeLandingPage = lazy(() => import('@modules/dashboard/HomeLandingPage'));
const HomePage = lazy(() => import('@modules/home'));
const RedirectPage = lazy(() => import('@modules/empty/RedirectPage'));
const StudentsPage = lazy(() => import('@modules/Students'));
const StudentCoursesPage = lazy(() => import('@modules/Students/CoursesPage'));
const BrandsPage = lazy(() => import('@modules/orders/BrandsPage'));
const CourseInboxPage = lazy(() => import('@modules/CourseInboxPage'));
const CourseInboxReplyPage = lazy(() => import('@modules/CourseInboxPage/CourseInboxReplyPage'));
const TimeOffPage = lazy(() => import('@modules/timeoff/TimeOffPage'));
const AutomationPage = lazy(() => import('@modules/automation/AutomationPage'));
const AutomationsPage = lazy(() => import('@modules/automation/AutomationsPage'));
const PresentationsPage = lazy(() => import('@modules/presentation/PresentationsPage'));
const PresentationPage = lazy(() => import('@modules/presentation/PresentationPage'));
const PresentationBuilderPage = lazy(() => import('@modules/presentation/PresentationPage/PresentationBuilder/PresentationBuilderPage'));
const ItemCategoriesPage = lazy(() => import('@modules/timeMaterials/ItemCategoriesPage'));
const AddEditProposalsPage = lazy(() => import('@modules/proposals/AddEditProposalPage'));
const MasterProductsPage = lazy(() => import('@modules/orders/MasterProductsPage'));
const AIDashboardFeedPage = lazy(() => import('@modules/aiDashboard/AIDashboardFeedPage'));
const AIDashboardProgressPage = lazy(() => import('@modules/aiDashboard/AIDashboardProgressPage'));

export const HelpArticleTags$ = new BehaviorSubject<HATAG[]>([]);

export const DashboardRouter = memo(() => {
  return (
    <Router>
      <AppContext.Provider value={GCApp.agency}>
        <Switch>
          <PrivateRoute exact path="/proposal-builder/:id" component={ProposalBuilderPage} roles={RoleService.staffRoles()} tags={['Proposal']} />
          <PrivateRoute exact path="/project-template/:id" component={ProposalBuilderPage} roles={RoleService.staffRoles()} tags={['Proposal']} />
          <PrivateRoute exact path="/project-list-template" component={ProjectListTemplateLoader} roles={RoleService.staffRoles()} tags={['Proposal']} />
          <PrivateRoute
            exact
            path="/proposal-templates/:folderId?"
            component={ProposalTemplatePage}
            roles={RoleService.staffRoles()}
            tags={['Proposal']}
          />
          <PrivateRoute
            exact
            path={['/proposals', '/proposals/:id', '/proposals/:status/:id', '/proposals/:id/:tab/view']}
            component={ProposalsPage}
            roles={RoleService.staffRoles()}
            tags={['Proposal']}
          />

          <PrivateRoute
            exact
            path={['/add-edit-proposal/:estimateId', '/add-edit-proposal/:estimateId/:id']}
            component={AddEditProposalsPage}
            roles={RoleService.staffRoles()}
            tags={['Proposal']}
          />

          <PrivateRoute exact path="/proposals/:id" component={ViewProposalPage} roles={RoleService.staffRoles()} tags={['Proposal']} />

          <PrivateRoute exact path="/companies" component={CompaniesPage} roles={RoleService.staffRoles()} tags={['Company', 'Brand']} />
          <PrivateRoute exact path="/companies/:id" component={CompanyPage} roles={RoleService.staffRoles()} tags={['Company', 'Brand']} />
          <PrivateRoute exact path="/companies/brand/:id" component={BrandDetailPage} roles={RoleService.staffRoles()} tags={['Company', 'Brand']} />

          <PrivateRoute
            exact
            path="/organizations"
            component={OrganizationsPage}
            roles={RoleService.staffRoles()}
            tags={['Organization', 'Contacts']}
          />
          <PrivateRoute
            exact
            path="/organizations-dashboard"
            component={OrganizationsDashboard}
            roles={RoleService.staffRoles()}
            tags={['Organization', 'Contacts']}
          />
          <PrivateRoute
            exact
            path="/client-dashboard"
            component={ClientDashboard}
            roles={RoleService.staffRoles()}
            tags={['Organization', 'Contacts']}
          />
          <PrivateRoute exact path="/estimates" component={EstimatesPage} roles={RoleService.staffRoles()} tags={['Organization', 'Contacts']} />
          <PrivateRoute exact path="/estimates/:id" component={EstimatePage} roles={RoleService.staffRoles()} tags={['Organization', 'Contacts']} />

          <PrivateRoute
            exact
            path="/subcontractor"
            component={SubContractorPage}
            roles={[UserRole.admin, UserRole.user, UserRole.subcontractor]}
            tags={['subcontractor']}
          />
          <PrivateRoute
            exact
            path="/subcontractor-dashboard"
            component={SubContractorDashboardPage}
            roles={[UserRole.admin, UserRole.user, UserRole.subcontractor]}
            tags={['subcontractor']}
          />
          <PrivateRoute
            exact
            path="/work-order-detail/:id/:tab?"
            component={WorkOrderDetailPage}
            roles={[UserRole.admin, UserRole.user, UserRole.subcontractor]}
            tags={['subcontractor']}
          />
          <PrivateRoute
            exact
            path="/subcontractor/:tab"
            component={SubContractorPage}
            roles={[UserRole.admin, UserRole.user, UserRole.subcontractor]}
            tags={['subcontractor']}
          />
          <PrivateRoute
            exact
            path="/client/portal-proposals"
            component={ClientProposalPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />
          <PrivateRoute
            exact
            path="/client/projects"
            component={ClientDashBoardPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />
          <PrivateRoute
            exact
            path="/client/work-orders"
            component={WorkOrderPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />
          <PrivateRoute
            exact
            path="/notifications"
            component={NotificationsPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal, UserRole.subcontractor]}
            tags={['clientPortal', 'subcontractor', 'Users']}
          />
          <PrivateRoute
            exact
            path="/client/proposals"
            component={ClientProposalPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />
          <PrivateRoute
            exact
            path="/client/project/:id"
            component={ProjectDashboardPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />
          <PrivateRoute
            exact
            // path="/client/work-order/:id"
            path={['/client/work-order/:id', '/client-work-order/:id']}
            component={WorkOrderDashboardPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />
          <PrivateRoute
            exact
            // path="/client/work-order/:id"
            path={['/client-work-order/:id/change-order-preview']}
            component={TMChangeOrderPreviewPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />
          <PrivateRoute
            exact
            path={['/client-work-order/:id/upgrade-preview']}
            component={UpgradeOptionPreviewPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />
          <PrivateRoute
            exact
            path={['/client-work-order/:id/list-item-preview']}
            component={ListItemPreviewPage}
            roles={[UserRole.admin, UserRole.user, UserRole.ClientPortal]}
            tags={['clientPortal']}
          />

          <PrivateRoute
            exact
            path="/organizations/:id/:tab?"
            component={OrganizationPage}
            roles={RoleService.staffRoles()}
            tags={['Organization', 'Contacts']}
          />
          <PrivateRoute
            exact
            path="/vendasta-accounts"
            component={VendastaAccountsPage}
            roles={[UserRole.admin]}
            tags={['Organization', 'Contacts']}
          />
          <PrivateRoute
            exact
            path="/vendasta-billing-report"
            component={VendastaBillingReportPage}
            roles={[UserRole.admin]}
            tags={['Organization', 'Contacts']}
          />

          <PrivateRoute exact path="/people" component={ContactsPage} roles={RoleService.staffRoles()} tags={['Organization', 'Contacts']} />
          <PrivateRoute exact path="/people/:id" component={ContactPage} roles={RoleService.staffRoles()} tags={['Organization', 'Contacts']} />

          <PrivateRoute
            exact
            path="/setting/template-builder/:id"
            component={TemplateBuilderPage}
            roles={RoleService.staffRoles()}
            tags={['Settings', 'Integrations', 'Permissions', 'Webhook', 'Import', 'Forms']}
          />
          <PrivateRoute
            exact
            path="/settings"
            component={SettingsPage}
            roles={RoleService.staffRoles()}
            tags={['Settings', 'Integrations', 'Permissions', 'Webhook', 'Import', 'Forms']}
          />
          <PrivateRoute
            exact
            path="/item-categories"
            component={ItemCategoriesPage}
            roles={RoleService.staffRoles()}
            tags={['Settings', 'itemCategory']}
          />
          <PrivateRoute
            exact
            path="/settings/:tab"
            component={SettingsPage}
            roles={RoleService.staffRoles()}
            tags={['Settings', 'Integrations', 'Permissions', 'Webhook', 'Import', 'Forms']}
          />
          <PrivateRoute
            exact
            path="/settings/:tab/:subTab"
            component={SettingsPage}
            roles={RoleService.staffRoles()}
            tags={['Settings', 'Integrations', 'Permissions', 'Webhook', 'Import', 'Forms']}
          />
          <PrivateRoute exact path="/my-account" component={MyAccountPage} tags={['Settings']} />
          <PrivateRoute exact path="/users" component={UsersPage} roles={RoleService.staffRoles()} tags={['Users']} />
          <PrivateRoute
            exact
            path="/users/review/:id/:subcontractorStatus?"
            component={ReviewSubContractorProfile}
            roles={RoleService.staffRoles()}
            tags={['Users']}
          />
          <PrivateRoute exact path="/activity-logs" component={ActivityLogPage} roles={RoleService.staffRoles()} tags={['Activity Log']} />
          <PrivateRoute exact path="/home-landing" component={HomeLandingPage} roles={RoleService.staffRoles()} tags={['Home']} />

          <PrivateRoute exact path="/user-roles" component={UserRolesPage} roles={RoleService.staffRoles()} tags={['Users', 'Permissions']} />
          <PrivateRoute
            exact
            path="/user-roles/permission/:id"
            component={UserRolePermissionPage}
            roles={RoleService.staffRoles()}
            tags={['Users', 'Permissions']}
          />

          {/* Projects */}
          <PrivateRoute exact path="/projects/create-project" component={CreateProjectPage} roles={RoleService.staffRoles()} tags={['Project']} />
          <PrivateRoute exact path="/projects/create-project/:id" component={CreateProjectPage} roles={RoleService.staffRoles()} tags={['Project']} />
          <PrivateRoute
            exact
            path="/projects/create-project/:id/:isProject"
            component={CreateSimpleProjectPage}
            roles={RoleService.staffRoles()}
            tags={['Project']}
          />
          <PrivateRoute exact path="/projects/edit/:orgId/:id" component={EditProjectPage} roles={RoleService.staffRoles()} tags={['Project']} />
          <PrivateRoute
            exact
            path="/subscriptions/create-subscription/:id"
            component={CreateSubscriptionPage}
            roles={RoleService.staffRoles()}
            tags={['Subscription']}
          />
          <PrivateRoute
            exact
            path="/subscriptions/one-time-payment/:orgId/:id"
            component={SubOneTimePayment}
            roles={RoleService.staffRoles()}
            tags={['Subscription']}
          />
          <PrivateRoute
            exact
            path="/subscriptions/edit/:orgId/:id"
            component={EditSubscription}
            roles={RoleService.staffRoles()}
            tags={['Subscription']}
          />

          <PrivateRoute
            exact
            path={['/board/shared/project/:id']}
            component={SharedProjectBoardPage}
            roles={RoleService.staffRoles()}
            tags={['Project', 'Contacts', 'Billing']}
          />

          <PrivateRoute
            exact
            path={['/board', '/board/project/:id', '/board/project/:id/task/:taskId', '/board/project/:id/task/:taskId/subtask/:subtaskId']}
            component={BoardPage}
            roles={RoleService.staffRoles()}
            tags={['Project', 'Contacts', 'Billing']}
          />
          <PrivateRoute
            exact
            path={['/shared-projects', '/shared-projects/:id']}
            component={SharedProjectsPage}
            roles={RoleService.staffRoles()}
            tags={['Project', 'Contacts', 'Billing']}
          />
          <PrivateRoute exact path="/project-template" component={TemplatesPage} roles={RoleService.staffRoles()} tags={['Project']} />
          <PrivateRoute exact path="/project-template/:id" component={TemplatePage} roles={RoleService.staffRoles()} tags={['Project']} />

          <PrivateRoute
            exact
            path="/subscription-board/checklist"
            component={CheckListPage}
            roles={RoleService.staffRoles()}
            tags={['Organization', 'Subscription']}
          />
          <PrivateRoute
            exact
            path="/subscription-board/checklist/:id"
            component={CheckListDetailPage}
            roles={RoleService.staffRoles()}
            tags={['Organization', 'Subscription']}
          />

          {/* Marketing */}

          {/* <PrivateRoute
            exact
            path={['/marketing-board', '/marketing-board/board/:id/:boardSlug?', '/marketing-board/:boardSlug?']}
            component={MarketingBoardPage}
            roles={RoleService.staffRoles()}
            tags={['Organization', 'Subscription']}
          /> */}

          <PrivateRoute
            exact
            path={[
              '/subscription-board',
              '/subscription-board/board/:id',
              '/subscription-board/board/:id/:type/:checklistId/:recurringChecklistId/task/:taskId',
              '/subscription-board/board/:id/:type/:checklistId/:recurringChecklistId/task/:taskId/subtask/:subtaskId',
            ]}
            component={MarketingBoardPageV2}
            roles={RoleService.staffRoles()}
            tags={['Organization', 'Subscription']}
          />

          {/* Support */}
          <PrivateRoute exact path="/support-tickets" component={TicketsPage} roles={RoleService.staffRoles()} tags={['Support']} />
          <PrivateRoute
            exact
            path={[
              '/support-tickets/detail/:id',
              '/support-tickets/detail/:id/task/:taskId',
              '/support-tickets/detail/:id/task/:taskId/subtask/:subtaskId',
            ]}
            component={TicketDetailPage}
            roles={RoleService.staffRoles()}
            tags={['Support']}
          />
          <PrivateRoute exact path="/support-tickets/my-tickets" component={MyTicketsPage} roles={RoleService.staffRoles()} tags={['Support']} />
          <PrivateRoute exact path="/support-tickets/category" component={SupportCategoryPage} roles={RoleService.staffRoles()} tags={['Support']} />
          <PrivateRoute exact path="/support-tickets/:tab" component={TicketsPage} roles={RoleService.staffRoles()} tags={['Support']} />

          {/* Regions Map */}
          <PrivateRoute exact path="/regions-map" component={RegionsMapPage} roles={RoleService.staffRoles()} tags={['Deals']} />

          {/* Deals */}
          <PrivateRoute exact path="/deals/new" component={AddDealPage} roles={RoleService.staffRoles()} tags={['Deals', 'Contacts']} />
          {/* Must precede /deals/:id?/:orgId?, which would otherwise treat "edit" as an org id */}
          <PrivateRoute exact path="/deals/:id/edit" component={EditDealPage} roles={RoleService.staffRoles()} tags={['Deals', 'Contacts']} />
          <PrivateRoute exact path="/deals/:id?/:orgId?" component={DealBoardPage} roles={RoleService.staffRoles()} tags={['Deals', 'Contacts']} />
          <PrivateRoute exact path="/deals/:id/:tab?" component={DealPage} roles={RoleService.staffRoles()} tags={['Deals', 'Contacts']} />

          <PrivateRoute exact path="/help-articles/:id?" component={HelpArticlesPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/help-article/category/:id" component={HelpArticlesPage} roles={RoleService.staffRoles()} />

          <PrivateRoute exact path="/help-article/:id" component={HelpArticlePage} roles={RoleService.staffRoles()} />

          <PrivateRoute exact path="/vendors" component={ServiceProviderPage} roles={RoleService.staffRoles()} tags={['Vendors']} />
          <PrivateRoute exact path={['/payables', '/bills']} component={PayablesPage} roles={RoleService.staffRoles()} tags={['Vendors']} />

          <PrivateRoute
            exact
            path={['/advanced-reporting', '/advanced-reporting/:id']}
            component={AdvancedReportingPage}
            roles={RoleService.staffRoles()}
            tags={['Reports']}
          />

          <PrivateRoute
            exact
            path="/reports-2/:moduleId?/:reportId?"
            component={DynamicReportPage}
            roles={RoleService.staffRoles()}
            tags={['Reports']}
          />

          <PrivateRoute exact path="/reports/:moduleId?/:reportId?" component={ReportsPage} roles={RoleService.staffRoles()} tags={['Reports']} />
          <PrivateRoute exact path="/company-overview" component={CompanyOverviewPage} roles={RoleService.staffRoles()} tags={['Reports']} />

          <PrivateRoute exact path="/media" component={MediaPage} roles={RoleService.staffRoles()} tags={['Media']} />
          <PrivateRoute exact path="/media/:id" component={MediaPage} roles={RoleService.staffRoles()} tags={['Media']} />

          <PrivateRoute exact path="/teams" component={TeamsPage} roles={RoleService.staffRoles()} tags={['Teams']} />

          <PrivateRoute exact path="/dashboard/:slug?" component={DashboardPage} roles={RoleService.staffRoles()} tags={['Dashboard']} />

          <PrivateRoute exact path="/projects" component={ClientProjects} roles={[UserRole.client]} tags={['Project']} />
          <PrivateRoute exact path="/project/:id/:appId?" component={ClientProject} roles={[UserRole.client]} tags={['Project']} />

          <PrivateRoute exact path="/client-home" component={ClientHomePage} roles={[UserRole.client]} tags={['Home']} />
          <PrivateRoute exact path="/home-2" component={HomePage} roles={RoleService.staffRoles()} tags={['Home']} />

          <PrivateRoute exact path="/forms" component={ClientForms} roles={[UserRole.client]} tags={['Settings', 'Forms']} />
          <PrivateRoute exact path="/form/:fileId" component={FormPage} roles={[UserRole.client]} tags={['Settings', 'Forms']} />
          <PrivateRoute exact path="/client-form/:fileId" component={ClientFormPage} roles={[UserRole.client]} tags={['Settings', 'Forms']} />
          <PrivateRoute exact path="/files" component={ClientMediaPage} roles={[UserRole.client]} tags={['Files']} />
          <PrivateRoute exact path="/files/detail/:id" component={ClientMediaPage} roles={[UserRole.client]} tags={['Files']} />

          {/* Changelogs */}

          <PrivateRoute exact path="/changelogs" component={ChangelogsPage} roles={RoleService.staffRoles()} />

          <PrivateRoute exact path="/tasks/:type?" component={TasksPage} roles={RoleService.staffRoles()} tags={['To-Dos']} />

          <PrivateRoute exact path="/ideas" component={IdeasPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/idea/:id" component={IdeaPage} roles={RoleService.staffRoles()} />

          {/* Orders */}
          <PrivateRoute exact path="/order/edit/:orgId/:id?" component={AddEditOrderPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/time-material/edit/:orgId/:id?" component={AddEditTimeMaterialPage} roles={RoleService.staffRoles()} />
          <PrivateRoute
            exact
            path="/order/invoice/:orgId/:orderId/:invoiceId?"
            component={AddEditOrderInvoicePage}
            roles={RoleService.staffRoles()}
          />
          <PrivateRoute
            exact
            path={['/order/po/:orderId/:poId/:type?', '/time-material/po/:orderId/:poId/:type?']}
            component={EditPOPage}
            roles={RoleService.staffRoles()}
          />
          <PrivateRoute exact path="/order/po-lading/:orderId/:poId" component={POLadingPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/order/po-picklist/:orderId/:poId" component={POPickListPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/time-material/po-lading/:orderId/:poId" component={POLadingPageTM} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/time-material/po-picklist/:orderId/:poId" component={POPickListPageTM} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path={['/orders', '/orders/detail/:id']} component={OrdersPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/orders/create/:orgId" component={AddEditOrderPage} roles={RoleService.staffRoles()} />

          <PrivateRoute exact path="/time-materials/create/:orgId" component={AddEditTimeMaterialPage} roles={RoleService.staffRoles()} />

          <PrivateRoute
            exact
            path={['/time-materials', '/time-materials/detail/:id']}
            component={TimeMaterialsPage}
            roles={RoleService.staffRoles()}
          />

          <PrivateRoute exact path="/time-material/edit/:orgId/:id?" component={AddEditTimeMaterialPage} roles={RoleService.staffRoles()} />
          <PrivateRoute
            exact
            path="/time-material/invoice/:orgId/:orderId/:boardId"
            component={AddEditTimeMaterialInvoicePage}
            roles={RoleService.staffRoles()}
          />

          {/* <PrivateRoute exact path="/time-material/change-order/:tmId" component={AddEditTMChangeOrderPage} roles={RoleService.staffRoles()} /> */}

          <PrivateRoute exact path={['/orders/suppliers', '/time-material/suppliers']} component={VendorsPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path={['/orders/brands', '/time-material/brands']} component={BrandsPage} roles={RoleService.staffRoles()} />
          <PrivateRoute
            exact
            path={['/orders/items', '/orders/items/:itemId', '/time-material/items', '/time-material/items/:itemId']}
            component={ProductsPage}
            roles={RoleService.staffRoles()}
          />

          <PrivateRoute exact path={['/master-items']} component={MasterProductsPage} roles={RoleService.staffRoles()} />

          <PrivateRoute
            exact
            path={['/orders/purchase-orders', '/time-material/purchase-orders']}
            component={PurchaseOrdersPage}
            roles={RoleService.staffRoles()}
          />

          <PrivateRoute exact path="/report-builder" component={EmptyPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/set-email" component={SetEmailPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/commissions" component={CommissionsPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/clock-ins" component={ClockinReports} roles={RoleService.staffRoles()} />

          <PrivateRoute exact path={['/orders/packages', '/time-material/packages']} component={PackagesPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path={['/time-material/templates']} component={TMTemplatesPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path={['/time-material/templates/:id']} component={TMTemplatePage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path={['/orders/package/:id', '/time-material/package/:id']} component={PackagePage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/redirect/:id?" component={RedirectPage} roles={RoleService.staffRoles()} />

          <PrivateRoute exact path="/automations" component={AutomationsPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/automations/:id" component={AutomationPage} roles={RoleService.staffRoles()} />

          <PrivateRoute exact path="/presentations" component={PresentationsPage} roles={RoleService.staffRoles()} />
          <PrivateRoute exact path="/presentations/:id" component={PresentationPage} roles={RoleService.staffRoles()} />

          {/* <Redirect
            exact
            from="/"
            to={
              Session.user && (Session.user.role === UserRole.admin || Session.user.role === UserRole.user)
                ? PermissionService.canSeeIt(Session.user, 'organizations')
                  ? '/organizations'
                  : '/home-landing'
                : '/client-home'
            }
          /> */}
          <Redirect
            exact
            from="/"
            to={(() => {
              if (Session.user) {
                if (Session.user.role === UserRole.admin) {
                  return PermissionService.canSeeIt(Session.user, 'organizations') ? '/organizations' : '/home-landing';
                } else if (Session.user.role === UserRole.user || Session.user.role === UserRole.salesRep) {
                  return '/home-landing';
                } else if (Session.user.role === UserRole.subcontractor) {
                  if (Session.user.subcontractorStatus == SubcontractorStatus.applicationApproved) {
                    return '/subcontractor-dashboard';
                  } else {
                    return '/subcontractor';
                  }
                } else if (Session.user.role === UserRole.ClientPortal) {
                  return '/client/work-orders';
                }
              }
              return '/client-home';
            })()}
          />

          {/* <Route path="*" exact component={NotFroundPage} /> */}
        </Switch>
      </AppContext.Provider>
      <AppContext.Provider value={GCApp.management}>
        <Switch>
          <PrivateRoute
            exact
            path={['/library', '/library/:id', '/library/:id/:mode/:type/:itemId']}
            component={LibraryPage}
            roles={RoleService.staffRoles()}
            tags={['Library']}
          />
          {/* <PrivateRoute exact path={['/board', '/board/project/:id']} component={BoardPage} roles={RoleService.staffRoles()} /> */}
          <PrivateRoute exact path="/checklists/:type?" component={ChecklistsPage} roles={RoleService.staffRoles()} tags={['To-Dos']} />
          <PrivateRoute
            exact
            path="/checklists/:slug/:type/:id"
            component={ChecklistTemplatePage}
            roles={RoleService.staffRoles()}
            tags={['To-Dos']}
          />
          {/* New courses section */}
          <PrivateRoute exact path="/course-builder/:id" component={CourseBuilderPage} roles={RoleService.staffRoles()} tags={['Training']} />
          <PrivateRoute exact path="/courses" component={CoursesPage} roles={RoleService.staffRoles()} tags={['Training']} />
          <PrivateRoute exact path="/courses/manage/:id/:type?" component={ManageCoursePage} roles={RoleService.staffRoles()} tags={['Training']} />
          <PrivateRoute exact path="/students" component={StudentsPage} roles={RoleService.staffRoles()} tags={['Training']} />
          <PrivateRoute
            exact
            path={['/instructor-inbox', '/student-inbox']}
            component={CourseInboxPage}
            roles={RoleService.staffRoles()}
            tags={['Training']}
          />
          <PrivateRoute
            exact
            path={['/instructor-inbox/:id', '/student-inbox/:id']}
            component={CourseInboxReplyPage}
            roles={RoleService.staffRoles()}
            tags={['Training']}
          />
          <PrivateRoute
            exact
            path={['/students/courses/:id', '/my-courses']}
            component={StudentCoursesPage}
            roles={RoleService.staffRoles()}
            tags={['Training']}
          />
          <PrivateRoute
            exact
            path={['/courses/view/:id', '/my-courses/view/:id']}
            component={CoursePage}
            roles={RoleService.staffRoles()}
            tags={['Training']}
          />
          <PrivateRoute exact path="/courses/preview/:id" component={PreviewCoursePage} roles={RoleService.staffRoles()} tags={['Training']} />
          <PrivateRoute exact path="/glossary" component={GlossaryPage} roles={RoleService.staffRoles()} tags={['Training']} />
          <PrivateRoute exact path="/line-up" component={LineUpPage} roles={RoleService.staffRoles()} tags={['Training']} />
          <PrivateRoute exact path="/line-up/:id" component={LineUpDetailPage} roles={RoleService.staffRoles()} tags={['Training']} />
          <PrivateRoute exact path="/todo/:appId" component={RedirectTodosPage} roles={RoleService.staffRoles()} tags={['To-Dos']} />
          <PrivateRoute exact path="/open-email" component={OpenEmailLinkPage} roles={RoleService.staffRoles()} />
          <PrivateRoute
            exact
            path="/open-project"
            component={OpenProjectLinkPage}
            roles={[UserRole.admin, UserRole.user, UserRole.client]}
            tags={['To-Dos']}
          />
          <PrivateRoute
            exact
            path={['/todos', '/todos/:tab', '/todos/:tab/task/:taskId', '/todos/:tab/checklist/:checklistId']}
            component={TodosPage}
            roles={RoleService.staffRoles()}
            tags={['To-Dos']}
          />
          <PrivateRoute exact path="/timeoff" component={TimeOffPage} />
          <PrivateRoute exact path="/all-forms/:type?/:id?" component={FormsPage} tags={['Forms']} />
          <PrivateRoute exact path="/management-form-templates" component={ManagementFormTemplatesPage} tags={['Forms']} />
          <PrivateRoute
            exact
            path="/presentation-builder/:presentationId/:stepId"
            component={PresentationBuilderPage}
            roles={RoleService.staffRoles()}
          />
          <PrivateRoute exact path="/ai-dashboard/feed" component={AIDashboardFeedPage} />;
          <PrivateRoute exact path="/ai-dashboard/progress" component={AIDashboardProgressPage} />;
          <PrivateRoute exact path="/empty-page" component={EmptyPage} roles={RoleService.staffRoles()} />
        </Switch>
      </AppContext.Provider>
    </Router>
  );
});
